import { Day, WeekDay } from '@mews/enums';
import { arrange } from '@mews/std';
import { mod, padStartZero } from '../func';
import toStructuredDate from './toStructuredDate';
import { DateLocalization, StructuredDate } from './types';

export interface StructuredDateServiceOptions {
    getLocalization: () => DateLocalization;
    getCurrentDate: () => StructuredDate;
    fromDateString: (dateString: string) => StructuredDate;
    isValidDateString: (dateString: string) => boolean;
    getDateString: (date: StructuredDate) => string;
}

export default ({
    getLocalization,
    isValidDateString,
    getCurrentDate,
    fromDateString,
    getDateString,
}: StructuredDateServiceOptions) => {
    const isDateLess = (
        { year, month, date }: StructuredDate,
        { year: year2, month: month2, date: date2 }: StructuredDate
    ): boolean =>
        year < year2 || (year === year2 && month < month2) || (year === year2 && month === month2 && date < date2);

    const isDateGreater = (
        { year, month, date }: StructuredDate,
        { year: year2, month: month2, date: date2 }: StructuredDate
    ): boolean =>
        year > year2 || (year === year2 && month > month2) || (year === year2 && month === month2 && date > date2);

    const isDateEqual = (
        { year, month, date }: StructuredDate,
        { year: year2, month: month2, date: date2 }: StructuredDate
    ): boolean => year === year2 && month === month2 && date === date2;

    const isDateGreaterOrEqual = (date1: StructuredDate, date2: StructuredDate): boolean =>
        isDateGreater(date1, date2) || isDateEqual(date1, date2);
    const isDateLessOrEqual = (date1: StructuredDate, date2: StructuredDate): boolean =>
        isDateLess(date1, date2) || isDateEqual(date1, date2);
    const isDateBetween = (date: StructuredDate, start: StructuredDate, end: StructuredDate): boolean =>
        isDateLess(start, date) && isDateLess(date, end);
    const isDateBetweenOrEqual = (date: StructuredDate, start: StructuredDate, end: StructuredDate): boolean =>
        isDateLessOrEqual(start, date) && isDateLessOrEqual(date, end);

    const isDateDisabled = (
        date: StructuredDate,
        { minDate, maxDate }: { minDate?: StructuredDate; maxDate?: StructuredDate } = {}
    ): boolean => (minDate && isDateLess(date, minDate)) || (maxDate && isDateGreater(date, maxDate)) || false;

    const getOrderedRange = (date1: StructuredDate, date2: StructuredDate): [StructuredDate, StructuredDate] =>
        isDateLess(date1, date2) ? [date1, date2] : [date2, date1];

    const getMinDate = (dates: Array<StructuredDate>): StructuredDate =>
        dates.reduce((min, cur) => (isDateLess(cur, min) ? cur : min));
    const getMaxDate = (dates: Array<StructuredDate>): StructuredDate =>
        dates.reduce((max, cur) => (isDateGreater(cur, max) ? cur : max));

    const addDays = ({ year, month, date }: StructuredDate, count: number): StructuredDate => {
        const newDate = new Date(Date.UTC(year, month, date + count));

        return toStructuredDate(newDate);
    };

    const addBusinessDays = (date: StructuredDate, count: number): StructuredDate => {
        let current = { ...date };
        let added = 0;

        while (added < count) {
            current = addDays(current, 1);
            const newDate = new Date(Date.UTC(current.year, current.month, current.date));
            const weekday = newDate.getUTCDay();

            if (weekday !== 0 && weekday !== 6) {
                added++;
            }
        }

        return current;
    };

    const getPrevDate = (date: StructuredDate): StructuredDate => addDays(date, -1);
    const getNextDate = (date: StructuredDate): StructuredDate => addDays(date, 1);

    const getPrevWeek = (date: StructuredDate): StructuredDate => addDays(date, -7);
    const getNextWeek = (date: StructuredDate): StructuredDate => addDays(date, 7);

    const getArrangedDayNames = (dayNames: string[]) => {
        const { weekStart } = getLocalization();
        return arrangeDayElements(dayNames, weekStart as WeekDay);
    };

    const getDayNames = (): string[] => getShortDayNames(); // deprecated

    const getShortDayNames = (): string[] => {
        const { dayShortNames } = getLocalization();
        return getArrangedDayNames(dayShortNames);
    };

    const getFullDayNames = (): string[] => {
        const { dayNames } = getLocalization();
        return getArrangedDayNames(dayNames);
    };

    // source https://cs.uwaterloo.ca/~alopez-o/math-faq/node73.html
    const getWeekdayIndex = (date: StructuredDate) => {
        const getMonthIndex = (m: number) => {
            switch (m) {
                case 0:
                    return 11;
                case 1:
                    return 12;
                default:
                    return m - 1;
            }
        };

        const k = date.date;
        const m = getMonthIndex(date.month);
        const C = parseInt(date.year.toString().slice(0, 2), 10);
        const year = date.month === 0 || date.month === 1 ? date.year - 1 : date.year;
        const Y = parseInt(year.toString().slice(2, 4), 10);

        return mod(k + Math.floor(2.6 * m - 0.2) - 2 * C + Y + Math.floor(Y / 4) + Math.floor(C / 4), 7);
    };

    const getWeekday = (date: StructuredDate): string => {
        const { dayNames } = getLocalization();
        const weekdayIndex = getWeekdayIndex(date);
        const arrangedDayNames = arrangeDayElements(dayNames, WeekDay.Sunday);

        return arrangedDayNames[weekdayIndex];
    };

    const getDateNumber = ({ date }: StructuredDate): number => date;

    const toISOString = ({ year, month, date }: StructuredDate): string =>
        `${year}-${padStartZero(month + 1)}-${padStartZero(date)}`;

    const getDiffNights = (date1: StructuredDate, date2: StructuredDate): number => {
        const newDate1 = new Date(Date.UTC(date1.year, date1.month, date1.date));
        const newDate2 = new Date(Date.UTC(date2.year, date2.month, date2.date));

        return Math.abs(newDate1.getTime() - newDate2.getTime()) / (1000 * 60 * 60 * 24);
    };

    return {
        addDays,
        addBusinessDays,
        getPrevDate,
        getNextDate,
        getPrevWeek,
        getNextWeek,
        getMinDate,
        getMaxDate,
        getDayNames, // deprecated
        getShortDayNames,
        getFullDayNames,
        getWeekdayIndex,
        getWeekday,
        getCurrentDate,
        getDateNumber,
        getDateFromDateString: fromDateString,
        getDateString,
        isValidDateString,
        isDateLess,
        isDateEqual,
        isDateBetween,
        isDateBetweenOrEqual,
        isDateGreater,
        isDateDisabled,
        getOrderedRange,
        isDateLessOrEqual,
        isDateGreaterOrEqual,
        toISOString,
        getDiffNights,
    };
};

const arrangeDayElements = (dayElements: string[], weekStart: WeekDay) => {
    const { value: weekStartIndex } = Day.getByName(weekStart);
    return arrange(dayElements, weekStartIndex);
};
